import React, { useState, useEffect, ReactNode } from 'react'
import Modal from '../Modal'
import CustomTableData from '../Table/TableData'
import CustomTableRow from '../Table/TableRow'
import CustomTableBody from '../Table/TableBody'
import CustomTable from '../Table'
import CustomTableHead from '../Table/TableHead'
import TablePagination from '../Table/Pagination'
import Empty from '../../assets/icons/Empty'
import DefaultButton from '../Button/Button'

interface AccessHistory {
  triggeredBy: string
  id: string
  dateTime: string
  action: string
  reason: string
  status: string
}

interface ResidentDTO {
  resident?: {
    address?: string
  }
}

interface AccessHistoryModalProps {
  open: boolean
  onClose: () => void
  resident?: ResidentDTO | null
  address?: string
  properties: AccessHistory[]
  propertyMemberId?: string
  onSelectAccessHistory: (item: AccessHistory) => void
}

const ITEMS_PER_PAGE = 6

const AccessHistoryModal: React.FC<AccessHistoryModalProps> = ({
  open,
  onClose,
  resident,
  address,
  properties,
  propertyMemberId,
  onSelectAccessHistory,
}) => {
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (open) setCurrentPage(1)
  }, [open])


  const tableHeadData = ['Date & Time', 'Action', 'Reason', 'Status', 'Trigger']

  const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE)

  const paginatedData = properties.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleRowClick = (item: AccessHistory) => {
    onClose()
    onSelectAccessHistory(item)
  }

  return (
    <Modal
      open={open}
      close={onClose}
      showClose={false}
      widthClass="w-[calc(100%-16px)] sm:w-[calc(100%-32px)] lg:w-[1040px]"
      modalClassName="p-3 sm:p-4 lg:p-3"
    >
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-[#212529]">Access History</h2>
          <p className="text-xs font-medium text-[#495057] sm:text-sm">
            {address || 'N/A'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close access history"
          className="-mt-1 text-xl leading-none text-[#ADB5BD] transition-colors hover:text-[#495057]"
        >
          ×
        </button>
      </header>

      <div className="mt-7 overflow-x-auto bg-[#F8F9FA]">
        <CustomTable className="min-w-[700px]">
          <CustomTableHead
            dataList={tableHeadData}
            cellClassName="!text-left !px-2 first:!pl-5 last:!pl-5"
          />

          {properties.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={tableHeadData.length}>
                  <div className="flex flex-col items-center justify-center h-[300px] gap-4">
                    <Empty />
                    <p className="text-sm font-semibold">No results found</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <CustomTableBody>
              {paginatedData.map((item, index) => (
                <CustomTableRow
                  key={item.id}
                  onClick={() => handleRowClick(item)}
                  className={`cursor-pointer ${
                    index % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FA]'
                  } hover:bg-gray-100`}
                >
                  <CustomTableData dataLabel="Date & Time" text={item.dateTime} />

                  <CustomTableData dataLabel="Action" text={item.action} />

                  <CustomTableData dataLabel="Reason" text={item.reason} />

                  <CustomTableData
                    dataLabel="Status"
                    text={item.status}
                    className={
                      item.status === 'Enabled'
                        ? 'text-green-600'
                        : item.status === 'Disabled'
                          ? 'text-red-600'
                          : ''
                    }
                  />

                  <CustomTableData dataLabel="Trigger" text={item.triggeredBy} />
                </CustomTableRow>
              ))}
            </CustomTableBody>
          )}
        </CustomTable>
      </div>

      <div className="mt-1">
        {properties.length > ITEMS_PER_PAGE && (
          <TablePagination
            totalPages={totalPages}
            currPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>

      <div className="mt-3 flex justify-end border-t pt-3">
        <DefaultButton
          buttonType="secondary"
          onClick={onClose}
          size="sm"
          className="mt-4"
        >
          Close
        </DefaultButton>
      </div>
    </Modal>
  )
}

export default AccessHistoryModal
